import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { ALL_OFFERINGS } from "@/data/catalog";

export function SectionHeading({ eyebrow, title, sub, center = false, dark = false }) {
  return (
    <div className={`max-w-3xl ${center ? "mx-auto text-center" : ""}`}>
      {eyebrow && <div className="text-xs uppercase tracking-widest text-[#25D366] font-semibold mb-3">{eyebrow}</div>}
      <h2 className={`font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>{title}</h2>
      {sub && <p className={`mt-4 text-lg ${dark ? "text-slate-300" : "text-slate-600"}`}>{sub}</p>}
    </div>
  );
}

/** "Why WhatsApp?" stats band. */
export function WhyWhatsApp() {
  const stats = [
    ["3B+", "people use WhatsApp every month"],
    ["98%", "average open rate for WhatsApp messages*"],
    ["45–60%", "typical click-through on button messages*"],
    ["24/7", "automated replies with chatbots"],
  ];
  return (
    <section className="py-20 md:py-24 bg-[#0F172A] relative overflow-hidden" data-testid="why-whatsapp">
      <div className="absolute inset-0 bg-dots opacity-20" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading dark center eyebrow="Why WhatsApp?" title="The one channel your customers actually read"
          sub="WhatsApp brings marketing, notifications and customer support together in the app your customers open dozens of times a day." />
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map(([v, l]) => (
            <div key={l} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center">
              <div className="font-display text-4xl sm:text-5xl font-bold text-[#25D366]">{v}</div>
              <div className="mt-2 text-sm text-slate-300">{l}</div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-slate-500">*Industry-reported averages; results vary by audience and message.</p>
      </div>
    </section>
  );
}

/** Numbered "how it works" steps. */
export function StepsRow({ steps }) {
  return (
    <ol className="grid md:grid-cols-3 gap-6">
      {steps.map(([title, desc], i) => (
        <li key={title} className="relative bg-white border border-slate-200 rounded-2xl p-6">
          <span className="w-9 h-9 rounded-full bg-[#25D366] text-white grid place-items-center text-sm font-bold">{i + 1}</span>
          <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{title}</h3>
          <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{desc}</p>
        </li>
      ))}
    </ol>
  );
}

/** "Everything you need" grid of every product/feature (minus `exclude`). */
export function PlatformGrid({ exclude, limit, title = "Everything you need for WhatsApp growth", sub = "One platform for marketing, sales, support and reviews." }) {
  let items = ALL_OFFERINGS.filter((o) => o.slug !== exclude);
  if (limit) items = items.slice(0, limit);
  return (
    <section className="py-20 md:py-24 bg-slate-50" data-testid="platform-grid">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="The GrabNChat platform" title={title} sub={sub} />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((o) => (
            <Link key={o.slug} to={`/products/${o.slug}`}
              className="group min-w-0 bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#25D366] hover:shadow-md hover:-translate-y-0.5 transition-all"
              data-testid={`platform-${o.slug}`}>
              <span className="w-10 h-10 rounded-xl grid place-items-center bg-[#25D366]/10 text-[#25D366]"><o.icon className="w-5 h-5" /></span>
              <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{o.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{o.short}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#25D366]">
                Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
        {limit && (
          <div className="mt-10 text-center">
            <Link to="/features" className="inline-flex items-center gap-2 font-semibold text-slate-900 hover:text-[#25D366]">
              See all features <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

/** Green "included with GrabNChat" strip. */
export function IncludedStrip() {
  const items = [
    "Free onboarding & setup help", "Meta Embedded Signup", "Green Tick assistance",
    "Free QR Kits", "Free WhatsApp link generator", "Service replies free of Meta charges",
  ];
  return (
    <section className="bg-[#25D366]" data-testid="included-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap justify-center gap-x-8 gap-y-3">
        {items.map((t) => (
          <span key={t} className="flex items-center gap-2 text-sm font-semibold text-white">
            <CheckCircle2 className="w-4 h-4" /> {t}
          </span>
        ))}
      </div>
    </section>
  );
}
